import { cn } from '@shared/components/shadcn';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import NextLink from 'next/link';
import { RichContent } from '../../RichContent/RichContent';
import { buildMagazinePageHref } from '../helpers/buildMagazinePageHref';

type AuthorProfileProps = {
  name: string;
  /** When absent, the read-more CTA is not rendered (author has no locale author page). */
  authorSlug?: string;
  /** Author bio as HTML from the CMS — sanitised via `RichContent` before render. */
  bio?: string;
  /** Resolved profile picture URL. Falls back to an initial-letter avatar when absent. */
  imageSrc?: string;
  className?: string;
  /**
   * Whether to render the "read all stories" CTA. Hidden on the author page itself (the visitor is
   * already viewing that author's articles), shown when the card appears alongside an article.
   */
  showReadMoreLink?: boolean;
};

function AuthorProfile({ name, authorSlug, bio, imageSrc, className, showReadMoreLink = true }: AuthorProfileProps) {
  const t = useTranslations('magazine.article');
  const showReadMore = showReadMoreLink && Boolean(authorSlug);

  return (
    <div
      className={cn('bg-background border-border flex flex-col items-center gap-6 rounded-lg border p-6 text-center lg:flex-row lg:items-stretch lg:gap-8 lg:text-left', className)}
      data-track-container="Profile"
      data-track-provider={name}
      data-testid="author-profile"
    >
      <div className="flex flex-row items-center gap-2 lg:flex-col lg:justify-center shrink-0">
        {imageSrc ? (
          <Image src={imageSrc} alt="" width={64} height={64} className="size-16 shrink-0 rounded-full object-cover" data-testid="author-profile-avatar-image" />
        ) : (
          <span
            className="bg-accent-green text-foreground dark:text-foreground-inverse paragraph-l-strong flex size-16 shrink-0 items-center justify-center rounded-full"
            aria-hidden="true"
            data-testid="author-profile-avatar"
          >
            {name.charAt(0).toUpperCase()}
          </span>
        )}
        <span className="paragraph-strong text-foreground">{name}</span>
      </div>
      <div className="flex flex-col items-center gap-6 lg:items-start lg:justify-center lg:gap-4">
        {bio && <RichContent information={bio} tag="div" className="author-profile-bio text-foreground" data-testid="author-profile-bio" />}
        {showReadMore && authorSlug && (
          <NextLink
            href={buildMagazinePageHref({ type: 'author', authorSlug }, 1)}
            className="link-label text-foreground underline"
            data-track-name="Read More"
            data-testid="author-profile-read-more"
          >
            {t('read_more_from_author', { name })}
          </NextLink>
        )}
      </div>
    </div>
  );
}

export { AuthorProfile, type AuthorProfileProps };
